import React, { useState, useEffect } from 'react';
import supabase from '../../utils/supabaseUtils';
import './TeamsList.css';

const TeamsList = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [teamToEdit, setTeamToEdit] = useState(null);
  const [formValues, setFormValues] = useState({
    name: '',
    type: 'team',
    sport: '',
    organization: '',
    primary_contact_id: '',
    primary_contact_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
    city: '',
    state: '',
    notes: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterSport, setFilterSport] = useState('all');

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      // Mock team data for development
      const mockTeams = [
        {
          id: '1',
          name: 'Denver Vikings',
          type: 'team',
          sport: 'Football',
          organization: 'Denver Vikings Youth Sports',
          primary_contact_id: '1',
          primary_contact_name: 'Sarah Johnson',
          primary_contact_email: 'sarah.johnson@example.com',
          primary_contact_phone: '(303) 555-1234',
          city: 'Denver',
          state: 'CO',
          notes: 'Youth football team for ages 10-14',
          created_at: '2023-05-15T08:30:00Z',
          updated_at: '2023-05-15T08:30:00Z'
        },
        {
          id: '2',
          name: 'Colorado Rapids Youth',
          type: 'club',
          sport: 'Soccer',
          organization: 'Colorado Rapids',
          primary_contact_id: '2',
          primary_contact_name: 'Michael Rodriguez',
          primary_contact_email: 'mrodriguez@example.com',
          primary_contact_phone: '(720) 555-5678',
          city: 'Commerce City',
          state: 'CO',
          notes: 'Official youth development program of the Colorado Rapids',
          created_at: '2023-04-20T10:15:00Z',
          updated_at: '2023-04-20T10:15:00Z'
        },
        {
          id: '3',
          name: 'Boulder Basketball Club',
          type: 'club',
          sport: 'Basketball',
          organization: 'Boulder Basketball Association',
          primary_contact_id: '5',
          primary_contact_name: 'Amanda Chen',
          primary_contact_email: 'achen@bolderbasketball.org',
          primary_contact_phone: '(720) 555-2233',
          city: 'Boulder',
          state: 'CO',
          notes: 'Youth and adult basketball programs in Boulder County',
          created_at: '2023-04-12T11:30:00Z',
          updated_at: '2023-04-12T11:30:00Z'
        },
        {
          id: '4',
          name: 'Eagles High School Athletics',
          type: 'school',
          sport: 'Multiple',
          organization: 'Eagles High School',
          primary_contact_id: '4',
          primary_contact_name: 'David Thompson',
          primary_contact_email: 'dthompson@eagleshigh.edu',
          primary_contact_phone: '(303) 555-3344',
          city: 'Highlands Ranch',
          state: 'CO',
          notes: 'High school athletic department covering all school sports',
          created_at: '2023-05-05T09:20:00Z',
          updated_at: '2023-05-05T09:20:00Z'
        },
        {
          id: '5',
          name: 'Denver Nuggets Youth',
          type: 'team',
          sport: 'Basketball',
          organization: 'Denver Nuggets Foundation',
          primary_contact_id: '',
          primary_contact_name: 'James Wilson',
          primary_contact_email: 'jwilson@nuggets.org',
          primary_contact_phone: '(303) 555-9988',
          city: 'Denver',
          state: 'CO',
          notes: 'Youth basketball program affiliated with the Denver Nuggets',
          created_at: '2023-03-15T13:45:00Z',
          updated_at: '2023-03-15T13:45:00Z'
        }
      ];
      
      setTeams(mockTeams);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching teams:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Validate required fields
      if (!formValues.name) {
        alert("Team name is required");
        setLoading(false);
        return;
      }
      
      if (teamToEdit) {
        // Update existing team
        const updatedTeams = teams.map(team => 
          team.id === teamToEdit.id 
            ? { 
                ...team, 
                ...formValues,
                updated_at: new Date().toISOString()
              }
            : team
        );
        setTeams(updatedTeams);
        
        // In production, you would update the team in Supabase
      } else {
        // Create new team
        const newTeam = {
          id: crypto.randomUUID(),
          ...formValues,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        setTeams([newTeam, ...teams]);
        
        // In production, you would insert the team in Supabase
      }
      
      resetForm();
    } catch (error) {
      console.error('Error saving team:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormValues({
      name: '',
      type: 'team',
      sport: '',
      organization: '',
      primary_contact_id: '',
      primary_contact_name: '',
      primary_contact_email: '',
      primary_contact_phone: '',
      city: '',
      state: '',
      notes: ''
    });
    setTeamToEdit(null);
    setShowTeamForm(false);
  };

  const handleEditTeam = (team) => {
    setTeamToEdit(team);
    setFormValues({
      name: team.name,
      type: team.type,
      sport: team.sport,
      organization: team.organization,
      primary_contact_id: team.primary_contact_id,
      primary_contact_name: team.primary_contact_name,
      primary_contact_email: team.primary_contact_email,
      primary_contact_phone: team.primary_contact_phone,
      city: team.city,
      state: team.state,
      notes: team.notes
    });
    setShowTeamForm(true);
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm("Are you sure you want to delete this team?")) {
      return;
    }
    
    try {
      // Remove from UI for immediate feedback
      setTeams(teams.filter(team => team.id !== teamId));
      
      // In production, you would delete from Supabase
      alert("Team deleted successfully");
    } catch (error) {
      console.error("Error deleting team:", error);
      alert(`Error: ${error.message}`);
      fetchTeams(); // Refresh to restore data
    }
  };

  const handleViewTeam = (teamId) => {
    // In a real implementation, this would navigate to a detailed team view
    alert(`View team details for team ID: ${teamId}`);
  };

  // Get available sports from the team data
  const availableSports = Array.from(new Set(teams.map(team => team.sport))).filter(Boolean);

  // Filter teams by type, sport, and search term
  const filteredTeams = teams.filter(team => {
    const matchesSearch = 
      team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.organization.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.primary_contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (team.city && team.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (team.state && team.state.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = filterType === 'all' || team.type === filterType;
    const matchesSport = filterSport === 'all' || team.sport === filterSport;
    
    return matchesSearch && matchesType && matchesSport;
  });

  // Format team type for display
  const formatTeamType = (type) => {
    const types = {
      'team': 'Team',
      'club': 'Club',
      'school': 'School',
      'league': 'League',
      'other': 'Other'
    };
    
    return types[type] || type;
  };

  return (
    <div className="teams-container">
      <div className="section-header">
        <h3>Teams & Clubs</h3>
        <div className="header-actions">
          <button 
            className="refresh-button"
            onClick={fetchTeams}
            title="Refresh Teams"
          >
            <i className="fas fa-sync-alt"></i>
          </button>
          <button 
            className="add-button"
            onClick={() => {
              setTeamToEdit(null);
              setFormValues({
                name: '',
                type: 'team',
                sport: '',
                organization: '',
                primary_contact_id: '',
                primary_contact_name: '',
                primary_contact_email: '',
                primary_contact_phone: '',
                city: '',
                state: '',
                notes: ''
              });
              setShowTeamForm(true);
            }}
          >
            <i className="fas fa-users"></i> Add Team
          </button>
        </div>
      </div>
      
      {/* Search and filter controls */}
      <div className="teams-controls">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search teams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button 
              className="clear-search" 
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        
        <div className="filter-group">
          <div className="filter-control">
            <select 
              value={filterType} 
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="team">Teams</option>
              <option value="club">Clubs</option>
              <option value="school">Schools</option>
              <option value="league">Leagues</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div className="filter-control">
            <select 
              value={filterSport} 
              onChange={(e) => setFilterSport(e.target.value)}
            >
              <option value="all">All Sports</option>
              {availableSports.map(sport => (
                <option key={sport} value={sport}>{sport}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Teams List */}
      {loading && teams.length === 0 ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading teams...</p>
        </div>
      ) : filteredTeams.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fas fa-users"></i>
          </div>
          <h3>No Teams Found</h3>
          <p>{searchTerm || filterType !== 'all' || filterSport !== 'all' ? 'Try adjusting your search or filters' : 'Add your first team to get started'}</p>
          {!searchTerm && filterType === 'all' && filterSport === 'all' && (
            <button 
              className="add-button"
              onClick={() => {
                setTeamToEdit(null);
                setFormValues({
                  name: '',
                  type: 'team',
                  sport: '',
                  organization: '',
                  primary_contact_id: '',
                  primary_contact_name: '',
                  primary_contact_email: '',
                  primary_contact_phone: '',
                  city: '',
                  state: '',
                  notes: ''
                });
                setShowTeamForm(true);
              }}
            >
              <i className="fas fa-users"></i> Add Team
            </button>
          )}
        </div>
      ) : (
        <div className="teams-card-grid">
          {filteredTeams.map(team => (
            <div className="team-card" key={team.id}>
              <div className="team-card-header">
                <div className={`team-type-badge ${team.type}`}>
                  {formatTeamType(team.type)}
                </div>
                <div className="team-actions">
                  <button 
                    className="team-action view"
                    onClick={() => handleViewTeam(team.id)}
                    title="View Team Details"
                  >
                    <i className="fas fa-eye"></i>
                  </button>
                  <button 
                    className="team-action edit"
                    onClick={() => handleEditTeam(team)}
                    title="Edit Team"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button 
                    className="team-action delete"
                    onClick={() => handleDeleteTeam(team.id)}
                    title="Delete Team"
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>
              
              <h3 className="team-name">{team.name}</h3>
              
              {team.organization && (
                <div className="team-org">{team.organization}</div>
              )}
              
              <div className="team-details">
                {team.sport && (
                  <div className="team-sport">
                    <i className="fas fa-basketball-ball"></i> {team.sport}
                  </div>
                )}
                
                {(team.city || team.state) && (
                  <div className="team-location">
                    <i className="fas fa-map-marker-alt"></i> {team.city}{team.city && team.state ? ', ' : ''}{team.state}
                  </div>
                )}
              </div>
              
              <div className="team-contact">
                <h4>Primary Contact</h4>
                {team.primary_contact_name ? (
                  <>
                    <div className="contact-name">{team.primary_contact_name}</div>
                    <div className="contact-methods">
                      {team.primary_contact_email && (
                        <a href={`mailto:${team.primary_contact_email}`} className="contact-method">
                          <i className="fas fa-envelope"></i> Email
                        </a>
                      )}
                      {team.primary_contact_phone && (
                        <a href={`tel:${team.primary_contact_phone}`} className="contact-method">
                          <i className="fas fa-phone"></i> Call
                        </a>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="no-contact">No contact assigned</div>
                )}
              </div>
              
              <div className="card-footer">
                <button className="text-button">
                  <i className="fas fa-comment-alt"></i> Log Communication
                </button>
                <button className="text-button">
                  <i className="fas fa-tasks"></i> View History
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Team Form Modal */}
      {showTeamForm && (
        <div className="modal-overlay">
          <div className="team-form-modal">
            <div className="modal-header">
              <h3>{teamToEdit ? 'Edit Team' : 'Add New Team'}</h3>
              <button 
                className="close-button"
                onClick={resetForm}
                disabled={loading}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Team Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formValues.name}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    placeholder="Team name"
                  />
                </div>
                
                <div className="form-group">
                  <label>Team Type</label>
                  <select
                    name="type"
                    value={formValues.type}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="team">Team</option>
                    <option value="club">Club</option>
                    <option value="school">School</option>
                    <option value="league">League</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Sport</label>
                  <input
                    type="text"
                    name="sport"
                    value={formValues.sport}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="e.g. Basketball, Football, etc."
                  />
                </div>
                
                <div className="form-group">
                  <label>Organization</label>
                  <input
                    type="text"
                    name="organization"
                    value={formValues.organization}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="Parent organization (if applicable)"
                  />
                </div>
              </div>
              
              <div className="form-section">
                <h4>Primary Contact</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Contact Name</label>
                    <input
                      type="text"
                      name="primary_contact_name"
                      value={formValues.primary_contact_name}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Contact Email</label>
                    <input
                      type="email"
                      name="primary_contact_email"
                      value={formValues.primary_contact_email}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Contact Phone</label>
                    <input
                      type="text"
                      name="primary_contact_phone"
                      value={formValues.primary_contact_phone}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-section">
                <h4>Location</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      name="city"
                      value={formValues.city}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      name="state"
                      value={formValues.state}
                      onChange={handleInputChange}
                      disabled={loading}
                      placeholder="e.g. CO, CA, etc."
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formValues.notes}
                  onChange={handleInputChange}
                  rows="3"
                  disabled={loading}
                  placeholder="Additional information about this team"
                ></textarea>
              </div>
              
              <div className="form-buttons">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="loading-text">
                      <span className="spinner-small"></span>
                      {teamToEdit ? 'Updating...' : 'Creating...'}
                    </span>
                  ) : (
                    teamToEdit ? 'Update Team' : 'Create Team'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamsList; 